// LT-135 — Account detail page. Editable fields (name/domain/industry);
// contacts under this account; rolled-up activity timeline (GET
// /accounts/{id}/activities — aggregated across attached contacts via
// the denormalised activities.account_id).

import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import PageLoader from '../../components/PageLoader';
import PageShell from '../../components/PageShell';
import { accountsAPI } from '../../services/accountsAPI';
import { opportunitiesAPI } from '../../services/opportunitiesAPI';
import type {
  Account,
  AccountUpdate,
  Activity,
  Contact,
} from '../../types/contact';
import type { Opportunity } from '../../types/pipeline';

const ACTIVITY_TYPE_LABELS: Record<string, string> = {
  call: 'Call',
  email: 'Email',
  sms: 'SMS',
  note: 'Note',
  meeting: 'Meeting',
};

const AccountDetailPage: React.FC = () => {
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();

  const [account, setAccount] = useState<Account | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    setErr(null);
    try {
      const [a, cs, acts, opps] = await Promise.all([
        accountsAPI.get(accountId),
        accountsAPI.listContacts(accountId),
        accountsAPI.listActivities(accountId),
        opportunitiesAPI.listForAccount(accountId).catch(() => [] as Opportunity[]),
      ]);
      setAccount(a);
      setContacts(cs);
      setActivities(acts);
      setOpportunities(opps);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load account');
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const applyPatch = async (patch: AccountUpdate) => {
    if (!accountId) return;
    setSaving(true);
    try {
      const updated = await accountsAPI.update(accountId, patch);
      setAccount(updated);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const softDelete = async () => {
    if (!accountId) return;
    if (!window.confirm('Delete this account? Attached contacts will be detached (not deleted).')) return;
    setSaving(true);
    try {
      await accountsAPI.softDelete(accountId);
      navigate('/accounts');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Delete failed');
      setSaving(false);
    }
  };

  if (loading) return <PageLoader />;
  if (!account) {
    return (
      <PageShell className="p-6">
        <div className="text-red-300">{err || 'Account not found.'}</div>
        <button onClick={() => navigate('/accounts')} className="mt-4 text-cyan-400 hover:text-cyan-300 hover:underline">
          Back to Accounts
        </button>
      </PageShell>
    );
  }

  return (
    <PageShell className="p-6 max-w-5xl">
      <button
        onClick={() => navigate('/accounts')}
        className="inline-flex items-center gap-1 text-sm text-cyan-400 hover:text-cyan-300 hover:underline mb-3"
      >
        <ArrowLeft size={14} /> Back to Accounts
      </button>

      {err && (
        <div className="mb-3 text-sm text-red-300 border border-red-500/30 rounded-lg px-3 py-2 bg-red-500/10">
          {err}
        </div>
      )}

      <div className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 border border-white/10 rounded-xl p-5 mb-5">
        <div className="flex items-start justify-between mb-4">
          <h1 className="text-xl font-semibold text-white">{account.name}</h1>
          <button
            onClick={softDelete}
            disabled={saving}
            className="text-sm text-red-400 hover:text-red-300 hover:underline disabled:text-gray-400 disabled:hover:no-underline transition-colors"
          >
            Delete account
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField label="Name" value={account.name} onSave={(v) => applyPatch({ name: v })} saving={saving} />
          <TextField label="Domain" value={account.domain || ''} onSave={(v) => applyPatch({ domain: v })} saving={saving} />
          <TextField label="Industry" value={account.industry || ''} onSave={(v) => applyPatch({ industry: v })} saving={saving} />
          <TextField label="Source" value={account.source || ''} onSave={(v) => applyPatch({ source: v })} saving={saving} />
        </div>
      </div>

      <div className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 border border-white/10 rounded-xl p-5 mb-5">
        <h2 className="text-lg font-semibold text-white mb-3">
          Contacts under this account ({contacts.length})
        </h2>
        {contacts.length === 0 ? (
          <div className="text-sm text-gray-400">No contacts attached.</div>
        ) : (
          <ul className="divide-y divide-white/10">
            {contacts.map((c) => {
              const name = [c.first_name, c.last_name].filter(Boolean).join(' ').trim() || '(no name)';
              return (
                <li key={c.id} className="py-2 flex items-center justify-between">
                  <div>
                    <Link to={`/contacts/${c.id}`} className="text-cyan-400 hover:text-cyan-300 hover:underline font-medium transition-colors">
                      {name}
                    </Link>
                    <div className="text-xs text-gray-400">
                      {c.email || '—'} · {c.phone || '—'} · <span className="capitalize">{c.lifecycle_stage}</span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* LT-152: Opportunities on this account */}
      <div className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 border border-white/10 rounded-xl p-5 mb-5">
        <h2 className="text-lg font-semibold text-white mb-3">
          Opportunities ({opportunities.length})
        </h2>
        {opportunities.length === 0 ? (
          <div className="text-sm text-gray-400">No deals tracked for this account.</div>
        ) : (
          <ul className="divide-y divide-white/10">
            {opportunities.map((o) => (
              <li key={o.id} className="py-2 flex items-center justify-between">
                <Link
                  to={`/deals/${o.id}`}
                  className="text-cyan-400 hover:text-cyan-300 hover:underline font-medium transition-colors"
                >
                  {o.name}
                </Link>
                <div className="text-xs text-gray-400 flex gap-3">
                  {o.amount !== null && o.amount !== undefined && (
                    <span>
                      {o.currency ?? ''} {Number(o.amount).toLocaleString()}
                    </span>
                  )}
                  {o.close_date && <span>Close {new Date(o.close_date).toLocaleDateString()}</span>}
                  {o.closed_at && <span className="text-emerald-300">closed</span>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 border border-white/10 rounded-xl p-5">
        <h2 className="text-lg font-semibold text-white mb-3">Rolled-up activity</h2>
        {activities.length === 0 ? (
          <div className="text-sm text-gray-400">No activity yet.</div>
        ) : (
          <ul className="divide-y divide-white/10">
            {activities.map((a) => {
              // LT-254: call rows link to the transcript viewer.
              const callTranscriptHref =
                a.type === 'call' && a.call_summary_id
                  ? `/calls/${a.call_summary_id}/transcript`
                  : null;
              return (
                <li key={a.id} className="py-3 flex items-start gap-3">
                  <span className="inline-block mt-1 px-2 py-0.5 rounded bg-white/10 border border-white/10 text-xs uppercase text-gray-300">
                    {ACTIVITY_TYPE_LABELS[a.type] || a.type}
                  </span>
                  <div className="flex-grow min-w-0">
                    {a.subject && <div className="text-sm font-medium text-white">{a.subject}</div>}
                    {(a.body || a.summary) && (
                      <div className="text-sm text-gray-300 whitespace-pre-wrap mt-1">
                        {a.body || a.summary}
                      </div>
                    )}
                    <div className="text-xs text-gray-400 mt-1">
                      <Link to={`/contacts/${a.contact_id}`} className="text-cyan-400 hover:text-cyan-300 hover:underline transition-colors">
                        contact
                      </Link>
                      {' · '}{a.direction} · {new Date(a.occurred_at).toLocaleString()}
                      {callTranscriptHref && (
                        <>
                          {' · '}
                          <Link
                            to={callTranscriptHref}
                            className="text-cyan-400 hover:text-cyan-300 hover:underline transition-colors"
                          >
                            view transcript
                          </Link>
                        </>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </PageShell>
  );
};

// ---- small editable field --------------------------------------------------

interface FieldProps {
  label: string;
  value: string;
  onSave: (v: string) => void;
  saving: boolean;
}

const TextField: React.FC<FieldProps> = ({ label, value, onSave, saving }) => {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-400 uppercase mb-1">{label}</label>
      <input
        type="text"
        value={v}
        disabled={saving}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => { if (v !== value) onSave(v); }}
        className="border border-white/10 bg-gray-800 text-white rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:border-cyan-500 disabled:opacity-50"
      />
    </div>
  );
};

export default AccountDetailPage;
